import clsx from "clsx";
import { type JSX, useEffect, useState } from "react";

import RenderIf from "common/components/RenderIf";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import useActiveConversationOwnerAccount from "../../../hooks/useActiveConversationOwnerAccount";
import Attachment from "./Attachment";
import AudioMessage from "./AudioMessage";
import EmojiReactions from "./EmojiReactions";
import LinkPreview from "./LinkPreview";
import MessageBubble from "./MessageBubble";
import Reactions from "./Reactions";
import TextMessage from "./TextMessage";

import type { ConversationMessage } from "../../../types";

type MessageProps = { isFirstMessage: boolean } & (
  | {
      isLoading: true;
      message?: never;
      showLeadPreview?: never;
      setEditMessage?: never;
    }
  | {
      isLoading?: false;
      message: ConversationMessage;
      showLeadPreview: (leadId: string) => void;
      setEditMessage: (message: ConversationMessage) => void;
    }
);

//10 seconds
const MESSAGE_READY_TIME = 10;

export default function Message({
  isFirstMessage,
  isLoading,
  ...rest
}: MessageProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { permissions } = useSelectedWorkspaceContext();
  const { activeConversation } = useActiveConversationContext();
  const { ownerAccount } = useActiveConversationOwnerAccount();

  const secondsSinceCreation = rest.message
    ? (Date.now() - rest.message.created_at) / 1000
    : 0;

  const [isMessageReady, setIsMessageReady] = useState(
    secondsSinceCreation > MESSAGE_READY_TIME,
  );

  useEffect(() => {
    //if message is not ready (scraped) on mount, update it based on MESSAGE_READY_TIME
    if (!isMessageReady) {
      const timeoutId = setTimeout(
        () => setIsMessageReady(true),
        (MESSAGE_READY_TIME - secondsSinceCreation) * 1000,
      );
      return () => clearTimeout(timeoutId);
    }
  }, []);

  const { participants, owner } = activeConversation;

  const isMine = isLoading
    ? !!Math.round(Math.random())
    : owner === rest.message.sender.id;

  let firstBubbleContent: JSX.Element;
  let emojiReactionsComponent: JSX.Element;

  if (isLoading) {
    const loaderBgColor = isMine ? "bg-purple-200" : "bg-black-200";

    firstBubbleContent = (
      <div
        className={clsx([
          "flex flex-col gap-1.5 mt-0.5",
          isMine ? "items-end justify-end" : "items-start justify-start",
        ])}
      >
        <Skeleton className={clsx(["h-3 w-48 rounded-3xl", loaderBgColor])} />
        <Skeleton className={clsx(["h-3 w-44 rounded-3xl", loaderBgColor])} />
        <Skeleton className={clsx(["h-3 w-36 rounded-3xl", loaderBgColor])} />
      </div>
    );
  } else {
    const { message } = rest;
    const { deleted: isDeleted, pending: isPending, body } = message;

    if (isDeleted) {
      firstBubbleContent = (
        <span className="italic mt-0.5">This message has been deleted</span>
      );
    } else if (isPending) {
      firstBubbleContent = body ? (
        <TextMessage>{body}</TextMessage>
      ) : (
        <span className="italic text-black-500">You sent an attachment</span>
      );
    } else {
      if ("audio" in message) {
        const { duration, url } = message.audio;

        firstBubbleContent = (
          <AudioMessage accountId={owner} audioUrl={url} duration={duration} />
        );
      } else if (body || "links" in message || "attachments" in message) {
        let secondaryContent: JSX.Element;

        if ("links" in message) {
          secondaryContent = (
            <LinkPreview isMine={isMine} link={message.links[0]} />
          );
        }
        if ("attachments" in message && message.attachments) {
          secondaryContent = (
            <Attachment
              attachment={message.attachments[0]}
              accountId={owner}
              isMine={isMine}
            />
          );
        }

        firstBubbleContent = (
          <div className="flex flex-col gap-2 w-full">
            {body && (
              <TextMessage
                highlight={
                  activeConversation.last_message.urn === message.urn &&
                  activeConversation.highlight &&
                  "body" in activeConversation.highlight
                    ? activeConversation.highlight.body[0]
                    : undefined
                }
              >
                {body}
              </TextMessage>
            )}
            {secondaryContent}
          </div>
        );
      } else {
        firstBubbleContent = (
          <span className="italic mt-0.5">Unsupported message type</span>
        );
      }
    }

    const { setEditMessage } = rest;

    const canModify =
      message.created_at >= Date.now() - 1000 * 60 * 60 && isMine;

    emojiReactionsComponent = secondsSinceCreation > 10 && (
      <EmojiReactions
        senderId={owner}
        isMenuVisible={canModify}
        message={message}
        setEditMessage={setEditMessage}
      />
    );
  }

  const messageComponents = [
    <MessageBubble
      key="message-bubble"
      participants={participants}
      isMine={isMine}
      message={isLoading ? undefined : rest.message}
      isFirstMessage={isFirstMessage}
      emojiReactionsComponent={emojiReactionsComponent}
    >
      {firstBubbleContent}
    </MessageBubble>,
  ];

  if (!isLoading) {
    const { message } = rest;
    // add all the extra attachment bubbles
    if ("attachments" in message && message.attachments?.length > 1) {
      const { attachments } = message;

      attachments.slice(1).forEach((attachment) => {
        messageComponents.push(
          <MessageBubble
            participants={participants}
            key={`message-bubble-${attachment.url}`}
            isMine={isMine}
            message={message}
            isFirstMessage={isFirstMessage}
            emojiReactionsComponent={emojiReactionsComponent}
          >
            {message.pending ? (
              <span className="italic text-black-500">
                You sent an attachment
              </span>
            ) : (
              <Attachment
                key={`attachment-content-${attachment.url}`}
                attachment={attachment}
                accountId={owner}
                isMine={isMine}
              />
            )}
          </MessageBubble>,
        );
      });
    }
  }

  const imageSrc = isMine
    ? ownerAccount?.picture_url
    : rest.message?.sender?.picture_url;

  const profileImageComponent = (
    <ProfileImage
      size="sm"
      className="mt-0.5 lg:mt-2"
      src={isLoading ? null : imageSrc}
    />
  );

  return (
    <div
      className={clsx(
        "my-1 flex gap-2 items-start",
        isMine
          ? ["flex-row-reverse", !isFirstMessage && "mr-10"]
          : ["flex-row", !isFirstMessage && "ml-10"],
      )}
    >
      <RenderIf condition={isFirstMessage}>
        {isLoading || isMine || !permissions.includes("leads_full") ? (
          profileImageComponent
        ) : (
          <button
            className="flex"
            type="button"
            onClick={() => rest.showLeadPreview(rest.message.sender.id)}
            aria-label="view participant details"
          >
            {profileImageComponent}
          </button>
        )}
      </RenderIf>
      <div
        className={clsx(
          "flex flex-col gap-2 overflow-hidden",
          isMine ? "items-end" : "items-start",
        )}
      >
        {messageComponents}
        {!isLoading && (rest.message.reactions || !isDesktop) && (
          <div className="flex justify-between -mt-1">
            {rest.message.reactions && (
              <Reactions accountId={owner} message={rest.message} />
            )}
            <RenderIf condition={!isDesktop}>
              {/* Reactions with default emojis on mobile */}
              <div className="ml-auto flex">{emojiReactionsComponent}</div>
            </RenderIf>
          </div>
        )}
      </div>
    </div>
  );
}
